import React, { useEffect, useState, useCallback } from "react";
import api from "../../services/api.tsx";
import CampaignModal from "../../components/admin/CampaignModal.tsx";
import Product from "../../Entities/Product.tsx";
import { useError } from "../../contexts/ErrorContext.tsx";
import ConfirmModal from "../../components/ConfirmModal.tsx";

const emptyCampaign = {
    id: "",
    name: "",
    startDate: "",
    endDate: "",
    minUserCount: 0,
    minTotalOrderQuantity: 0,
    maxUserCount: 0,
    maxTotalOrderQuantity: 0,
    discountRate: 0,
    depositRate: 0,
    isActive: false,
    products: [] as { productId: string; discountedQuantity: number }[],
    productIds: [] as string[],
};

const CampaignManagementPage = () => {
    const [campaigns, setCampaigns] = useState<any[]>([]);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editCampaign, setEditCampaign] = useState(emptyCampaign);
    const [newCampaign, setNewCampaign] = useState({ ...emptyCampaign });
    const [allProducts, setAllProducts] = useState<Product[]>([]);
    const [showModal, setShowModal] = useState(false);
    const { setError } = useError();
    const [confirmModal, setConfirmModal] = useState<{ show: boolean; campaignId?: string }>({ show: false });

    // Fetch all products for selection
    useEffect(() => {
        api.get("/api/product").then(res => setAllProducts(res.data));
    }, []);

    const fetchCampaigns = useCallback(async () => {
        try {
            const token = localStorage.getItem("access_token");
            const res = await api.get("/api/campaign/all-campaigns", {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCampaigns(res.data);
        } catch (err) {
            setError(err.response?.data?.Message || "Bir hata oluştu.");
        }
    }, [setError]);

    useEffect(() => {
        fetchCampaigns();
    }, [fetchCampaigns]);

    // Add product to campaign
    const handleAddProduct = () => {
        setNewCampaign({
            ...newCampaign,
            products: [...newCampaign.products, { productId: "", discountedQuantity: 0 }],
        });
    };

    // Update product in campaign
    const handleProductChange = (idx: number, field: string, value: any) => {
        const updated = [...newCampaign.products];
        updated[idx] = { ...updated[idx], [field]: value };
        setNewCampaign({ ...newCampaign, products: updated });
    };

    // Remove product from campaign
    const handleRemoveProduct = (idx: number) => {
        const updated = [...newCampaign.products];
        updated.splice(idx, 1);
        setNewCampaign({ ...newCampaign, products: updated });
    };

    // CREATE
    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        // Check required text fields
        if (
            !newCampaign.name ||
            !newCampaign.startDate ||
            !newCampaign.endDate
        ) {
            alert("Lütfen tüm zorunlu alanları doldurun.");
            return;
        }

        // Check number fields
        const numberFields = [
            "minUserCount",
            "minTotalOrderQuantity",
            "maxUserCount",
            "maxTotalOrderQuantity",
            "discountRate",
            "depositRate",
        ];
        for (const field of numberFields) {
            if (
                newCampaign[field] === "" ||
                newCampaign[field] === null ||
                isNaN(newCampaign[field]) ||
                Number(newCampaign[field]) < 0
            ) {
                alert("Sayısal alanlar boş olamaz ve 0'dan küçük olamaz.");
                return;
            }
        }

        // Check products
        if (!newCampaign.products.length) {
            alert("En az bir kampanya ürünü eklemelisiniz.");
            return;
        }
        for (const prod of newCampaign.products) {
            if (!prod.productId || prod.discountedQuantity === null || isNaN(prod.discountedQuantity) || Number(prod.discountedQuantity) < 0) {
                alert("Ürün ve indirimli miktar boş olamaz ve 0'dan küçük olamaz.");
                return;
            }
        }

        try {
            const token = localStorage.getItem("access_token");
            await api.post("/api/campaign", {
                name: newCampaign.name,
                startDate: newCampaign.startDate,
                endDate: newCampaign.endDate,
                minUserCount: newCampaign.minUserCount,
                minTotalOrderQuantity: newCampaign.minTotalOrderQuantity,
                maxUserCount: newCampaign.maxUserCount,
                maxTotalOrderQuantity: newCampaign.maxTotalOrderQuantity,
                discountRate: newCampaign.discountRate,
                depositRate: newCampaign.depositRate,
                products: newCampaign.products,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setNewCampaign({ ...emptyCampaign });
            // fetchCampaigns(); // call your fetch function here
        } catch (err) {
            alert("Kampanya eklenemedi.");
            setError(err.response?.data?.Message || "Bir hata oluştu.");
        }
    };

    // DELETE
    const handleDelete = async (id: string) => {
        setConfirmModal({ show: true, campaignId: id });
    };

    const confirmDelete = async () => {
        if (!confirmModal.campaignId) return;
        try {
        const token = localStorage.getItem("access_token");
        await api.delete(`/api/campaign/${confirmModal.campaignId}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        fetchCampaigns();
        } catch (err) {
        alert("Kampanya silinemedi.");
        setError(err.response?.data?.Message || "Bir hata oluştu.");
        } finally {
        setConfirmModal({ show: false });
        }
    };

    // EDIT
    const startEdit = (campaign: any) => {
        setEditingId(campaign.id);
        setEditCampaign({
            id: campaign.id,
            name: campaign.name,
            startDate: campaign.startDate?.slice(0, 10) || "",
            endDate: campaign.endDate?.slice(0, 10) || "",
            minUserCount: campaign.minUserCount ?? 0,
            minTotalOrderQuantity: campaign.minTotalOrderQuantity ?? 0,
            maxUserCount: campaign.maxUserCount ?? 0,
            maxTotalOrderQuantity: campaign.maxTotalOrderQuantity ?? 0,
            discountRate: campaign.discountRate ?? 0,
            depositRate: campaign.depositRate ?? 0,
            isActive: campaign.isActive ?? false,
            products: campaign.products ?? [],
            productIds: campaign.products?.map((p: any) => p.productId) || [],
        });
    };

    const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        if (name === "isActive") {
            setEditCampaign({ ...editCampaign, isActive: checked });
        } else {
            setEditCampaign({ ...editCampaign, [name]: value });
        }
    };

    const handleEditSave = async (id: string) => {
        try {
            const token = localStorage.getItem("access_token");
            await api.put(`/api/campaign`, editCampaign, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setEditingId(null);
            fetchCampaigns();
        } catch (err) {
            alert("Kampanya güncellenemedi.");
            setError(err.response?.data?.Message || "Bir hata oluştu.");
        }
    };

    return (
        <div className="p-8 w-full bg-gray-100 min-h-screen">
            <h2 className="text-xl font-bold mb-3">Kampanya Yönetimi</h2>
            <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mb-4"
                onClick={() => setShowModal(true)}
            >
                Kampanya Ekle
            </button>

            <CampaignModal
                show={showModal}
                onClose={() => setShowModal(false)}
                onSubmit={e => { handleAdd(e); setShowModal(false); }}
                newCampaign={newCampaign}
                setNewCampaign={setNewCampaign}
                allProducts={allProducts}
                handleAddProduct={handleAddProduct}
                handleProductChange={handleProductChange}
                handleRemoveProduct={handleRemoveProduct}
            />

            <table className="min-w-full bg-white border rounded text-sm">
                <thead>
                    <tr className="bg-gray-100 text-left">
                        <th className="py-1 px-2 border-bw-[120px]">Kampanya Adı</th>
                        <th className="py-1 px-2 border-bw-[80px]">İndirim Oranı</th>
                        <th className="py-1 px-2 border-bw-[80px]">Depozit Oranı</th>
                        <th className="py-1 px-2 border-bw-[80px]">Min Kullanıcı</th>
                        <th className="py-1 px-2 border-bw-[80px]">Max Kullanıcı</th>
                        <th className="py-1 px-2 border-bw-[80px]">Min Sipariş</th>
                        <th className="py-1 px-2 border-bw-[80px]">Max Sipariş</th>
                        <th className="py-1 px-2 border-bw-[80px]">Başlangıç</th>
                        <th className="py-1 px-2 border-bw-[80px]">Bitiş</th>
                        <th className="py-1 px-2 border-bw-[80px]">Aktif Mi</th>
                        <th className="py-1 px-2 border-bw-[120px]">Ürünler</th>
                        <th className="py-1 px-2 border-bw-[80px]">İşlemler</th>
                    </tr>
                </thead>
                <tbody>
                    {campaigns.map((campaign: any) => (
                        <tr key={campaign.id}>
                            {editingId === campaign.id ? (
                                <>
                                    <td className="py-1 px-2 border-b">
                                        <input
                                            type="text"
                                            name="name"
                                            value={editCampaign.name}
                                            onChange={handleEditChange}
                                            className="border rounded px-2 py-1 max-w-[150px]"
                                        />
                                    </td>
                                    <td className="py-1 px-2 border-b">
                                        <input
                                            type="number"
                                            name="discountRate"
                                            value={editCampaign.discountRate}
                                            onChange={handleEditChange}
                                            className="border rounded px-2 py-1 w-20"
                                            min={0}
                                            max={100}
                                        />
                                    </td>
                                    <td className="py-1 px-2 border-b">
                                        <input
                                            type="number"
                                            name="depositRate"
                                            value={editCampaign.depositRate}
                                            onChange={handleEditChange}
                                            className="border rounded px-2 py-1 w-20"
                                            min={0}
                                            max={100}
                                        />
                                    </td>
                                    <td className="py-1 px-2 border-b">
                                        <input
                                            type="number"
                                            name="minUserCount"
                                            value={editCampaign.minUserCount}
                                            onChange={handleEditChange}
                                            className="border rounded px-2 py-1 w-20"
                                            min={1}
                                            max={10000}
                                        />
                                    </td>
                                    <td className="py-1 px-2 border-b">
                                        <input
                                            type="number"
                                            name="mazUserCount"
                                            value={editCampaign.maxUserCount}
                                            onChange={handleEditChange}
                                            className="border rounded px-2 py-1 w-20"
                                            min={1}
                                            max={100000}
                                        />
                                    </td>
                                    <td className="py-1 px-2 border-b">
                                        <input
                                            type="number"
                                            name="minTotalOrderQuantity"
                                            value={editCampaign.minTotalOrderQuantity}
                                            onChange={handleEditChange}
                                            className="border rounded px-2 py-1 w-20"
                                            min={1}
                                            max={1000000}
                                        />
                                    </td>
                                    <td className="py-1 px-2 border-b">
                                        <input
                                            type="number"
                                            name="maxTotalOrderQuantity"
                                            value={editCampaign.maxTotalOrderQuantity}
                                            onChange={handleEditChange}
                                            className="border rounded px-2 py-1 w-20"
                                            min={1}
                                            max={100000000}
                                        />
                                    </td>
                                    <td className="py-1 px-2 border-b">
                                        <input
                                            type="date"
                                            name="startDate"
                                            value={editCampaign.startDate}
                                            onChange={handleEditChange}
                                            className="border rounded px-2 py-1"
                                        />
                                    </td>
                                    <td className="py-1 px-2 border-b">
                                        <input
                                            type="date"
                                            name="endDate"
                                            value={editCampaign.endDate}
                                            onChange={handleEditChange}
                                            className="border rounded px-2 py-1"
                                        />
                                    </td>
                                    <td className="py-1 px-2 border-b">
                                        <input
                                            type="checkbox"
                                            name="isActive"
                                            checked={!!editCampaign.isActive}
                                            onChange={handleEditChange}
                                            className="form-checkbox h-5 w-5 text-green-600"
                                        />
                                    </td>
                                    <td className="py-1 px-2 border-b">
                                        {campaign.productIds && campaign.productIds.length > 0
                                            ? campaign.productIds
                                                .map((p: any) => {
                                                    const prod = allProducts.find(ap => ap.id === p);
                                                    return prod ? prod.name + "*" + prod.category + "*" + prod.brand : "Bilinmeyen Ürün";
                                                })
                                                .join(", ")
                                            : <span className="text-gray-400">Yok</span>
                                        }
                                    </td>
                                    <td className="py-1 px-2 border-b">
                                        <button
                                            className="text-green-600 hover:underline mr-2"
                                            onClick={() => handleEditSave(campaign.id)}
                                        >
                                            Kaydet
                                        </button>
                                        <button
                                            className="text-gray-600 hover:underline"
                                            onClick={() => setEditingId(null)}
                                        >
                                            İptal
                                        </button>
                                    </td>
                                </>
                            ) : (
                                <>
                                    <td className="py-1 px-2 border-b">{campaign.name}</td>
                                    <td className="py-1 px-2 border-b">%{campaign.discountRate}</td>
                                    <td className="py-1 px-2 border-b">%{campaign.depositRate}</td>
                                    <td className="py-1 px-2 border-b">{campaign.minUserCount}</td>
                                    <td className="py-1 px-2 border-b">{campaign.maxUserCount}</td>
                                    <td className="py-1 px-2 border-b">{campaign.minTotalOrderQuantity}</td>
                                    <td className="py-1 px-2 border-b">{campaign.maxTotalOrderQuantity}</td>
                                    <td className="py-1 px-2 border-b">{campaign.startDate?.slice(0, 10)}</td>
                                    <td className="py-1 px-2 border-b">{campaign.endDate?.slice(0, 10)}</td>
                                    <td className="py-1 px-2 border-b">
                                        {campaign.isActive ? (
                                            <span className="text-green-600 font-semibold">Aktif</span>
                                        ) : (
                                            <span className="text-red-600 font-semibold">Pasif</span>
                                        )}
                                    </td>
                                    <td className="py-1 px-2 border-b">
                                        {campaign.productIds && campaign.productIds.length > 0
                                            ? campaign.productIds
                                                .map((p: any) => {
                                                    const prod = allProducts.find(ap => ap.id === p);
                                                    return prod ? prod.name + "*" + prod.category + "*" + prod.brand : "Bilinmeyen Ürün";
                                                })
                                                .join(", ")
                                            : <span className="text-gray-400">Yok</span>
                                        }
                                    </td>
                                    <td className="py-1 px-2 border-b">
                                        <button
                                            className="text-blue-600 hover:underline mr-2"
                                            onClick={() => startEdit(campaign)}
                                        >
                                            Düzenle
                                        </button>
                                        <button
                                            className="text-red-600 hover:underline"
                                            onClick={() => handleDelete(campaign.id)}
                                        >
                                            Sil
                                        </button>
                                    </td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
            <ConfirmModal
                show={confirmModal.show}
                title="Kampanya Sil"
                message="Bu kampanyayı silmek istediğinize emin misiniz?"
                onConfirm={confirmDelete}
                onCancel={() => setConfirmModal({ show: false })}
                confirmText="Sil"
                cancelText="Vazgeç"
            />
        </div>
    );
};

export default CampaignManagementPage;